import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("pr_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response && err.response.status === 401) {
      localStorage.removeItem("pr_token");
      localStorage.removeItem("pr_user");
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export default api;

export const auth = {
  login: (username, password) => api.post("/auth/login", { username, password }),
  me: () => api.get("/me"),
  myAccess: () => api.get("/me/access"),
  // Silent JWT extension. The client calls this on app boot (or a periodic
  // heartbeat) so an active user never gets "token expired" on the next
  // request. If the current token is already expired the backend returns
  // 401 and the axios interceptor bounces the user to /login — which is the
  // desired behaviour (auto-refresh must never resurrect an expired session).
  refresh: () => api.post("/auth/refresh"),
  logout: () => {
    localStorage.removeItem("pr_token");
    localStorage.removeItem("pr_user");
    window.location.href = "/login";
  },
  user: () => {
    try { return JSON.parse(localStorage.getItem("pr_user") || "null"); }
    catch { return null; }
  },
  setSession: (token, user) => {
    localStorage.setItem("pr_token", token);
    localStorage.setItem("pr_user", JSON.stringify(user));
  },
};

export const prs = {
  list: (params) => api.get("/prs", { params }),
  get: (id) => api.get(`/prs/${id}`),
  kpis: (params) => api.get("/kpis", { params }),
  // Combined endpoint (Feb 2026) — returns {rows, kpi} in one DB roundtrip.
  // Pass {...params, fresh: true} right after a write to bypass the 20s cache.
  dashboard: (params) => api.get("/dashboard", { params }),
  decide: (voc_no, action, note) =>
    api.post("/prs/decision", { voc_no, action, note }),
  unhold: (voc_no) => api.post(`/prs/${voc_no}/unhold`),
  cancel: (voc_no, reason) => api.post(`/prs/${voc_no}/cancel`, { reason }),
  decideBulk: (voc_nos, action, note) =>
    api.post("/prs/decision/bulk", { voc_nos, action, note }),
  reasoning: (voc_no) => api.post("/prs/reasoning", { voc_no }),
  itemDecision: (voc_no, icode, iname, action) =>
    api.post("/prs/item-decision", { voc_no, icode, iname, action }),
  autoDecide: (voc_no) => api.post("/prs/auto-decide", { voc_no }),
  finalize: (voc_no) => api.post("/prs/finalize", { voc_no }),
  savings: (params) => api.get("/savings", { params }),
  authorise: (voc_no, action, note) =>
    api.post("/prs/authorise", { voc_no, action, note }),
  authoriseBulk: (voc_nos, action, note) =>
    api.post("/prs/authorise/bulk", { voc_nos, action, note }),
  timeline: (voc_no) => api.get(`/prs/${voc_no}/timeline`),
};

// Email one-click Approve/Reject landing flow. `preview` runs on page load
// (safe, no side-effects); `confirm` executes the decision. Both take the
// signed JWT from the email URL — the backend enforces device-cookie match
// and single-use consumption to defeat email forwarding.
export const emailAction = {
  preview: (token) => api.post("/email-action/preview", { token }),
  confirm: (token, reason) => api.post("/email-action/confirm", { token, reason: reason || undefined }),
};

export const admin = {
  listLogins: () => api.get("/admin/logins"),
  candidates: () => api.get("/admin/approver-candidates"),
  createLogin: (data) => api.post("/admin/logins", data),
  updateLogin: (username, data) => api.patch(`/admin/logins/${username}`, data),
  deleteLogin: (username) => api.delete(`/admin/logins/${username}`),
  dbInfo: () => api.get("/admin/db/info"),
  dbQuery: (sql, limit = 200) => api.post("/admin/db/query", { sql, limit }),
  dbExec: (sql) => api.post("/admin/db/exec", { sql, confirm: true }),
  listPolicies: () => api.get("/admin/approval-policies"),
  createPolicy: (data) => api.post("/admin/approval-policies", data),
  updatePolicy: (id, data) => api.patch(`/admin/approval-policies/${id}`, data),
  deletePolicy: (id) => api.delete(`/admin/approval-policies/${id}`),
  backfillAi: (dryRun = true) => api.post("/admin/backfill-ai-results", { dry_run: dryRun }),
  backfillStatus: () => api.get("/admin/backfill-ai-results/status"),
  cacheStatus: () => api.get("/admin/cache/status"),
  cacheSync: () => api.post("/admin/cache/sync"),
  cacheSyncItemMaster: () => api.post("/admin/cache/sync/items/item-master"),
  cacheSyncGpurci: () => api.post("/admin/cache/sync/items/gpurci"),
  listAutoRules: () => api.get("/admin/auto-approve-rules"),
  autoRulesAudit: () => api.get("/admin/auto-approve-rules/audit"),
  createAutoRule: (data) => api.post("/admin/auto-approve-rules", data),
  updateAutoRule: (id, data) => api.put(`/admin/auto-approve-rules/${id}`, data),
  deleteAutoRule: (id) => api.delete(`/admin/auto-approve-rules/${id}`),
};

export const create = {
  searchItems: (q) => api.get("/items/search", { params: { q, limit: 25 } }),
  sapMatchIcodes: (icodes) => api.post("/items/sap-match", { icodes }),
  // Batch check: is each icode present in any Released BOM?
  // Returns `{ icode: bool }`. Used by CreatePR to render a soft-warn
  // "Not in any BOM" pill on BOP lines and by NewPriceDrawer to
  // hard-block LP creation for non-BOM materials.
  bomCheckIcodes: (icodes) =>
    api.post("/items/bom-check", { item_codes: icodes })
       .then((r) => ({ data: r.data || {} })),
  // Batch check: is each icode's SAP Purchasing view "Active"?
  // Returns `{ icode: { active: bool, status_code: str|null,
  // status_label: str } }`. Used by both CreatePR (rose pill + block
  // Submit on BOP lines whose Purchasing view is Blocked / In Prep
  // / Obsolete / missing) and NewPriceDrawer (hard-block Save).
  purchasingCheckIcodes: (icodes) =>
    api.post("/items/purchasing-check", { item_codes: icodes })
       .then((r) => ({ data: r.data || {} })),
  quoteBopItems: (pcode, purc_type, items) => api.post("/pr/quote", { pcode, purc_type, items }),
  fetchLpLive: (pcode, icode, qty, unit) => api.post("/pr/lp-fetch-live", { pcode, icode, qty, unit }),
  searchSuppliers: (q) => api.get("/suppliers/search", { params: { q, limit: 25 } }),
  searchEmployees: (q) => api.get("/pr/search-employees", { params: { q } }),
  listEmployees: () => api.get("/pr/employees"),
  nextVocNo: () => api.get("/pr/next-voc-no"),
  preview: (draft) => api.post("/pr/preview-score", draft),
  listDrafts: () => api.get("/pr/drafts"),
  saveDraft: (draft) => api.post("/pr/drafts", draft),
  updateDraft: (id, draft) => api.put(`/pr/drafts/${id}`, draft),
  deleteDraft: (id) => api.delete(`/pr/drafts/${id}`),
  submitDraft: (id) => api.post(`/pr/drafts/${id}/submit`),
  myPRs: (params) => api.get("/pr/my-prs", { params: params || {} }),
  dismissMyPR: (vocNo) => api.post(`/pr/my-prs/${vocNo}/dismiss`),
  restoreMyPR: (vocNo) => api.post(`/pr/my-prs/${vocNo}/restore`),
  cloneRejected: (vocNo) => api.post(`/pr/drafts/clone-from/${vocNo}`),
  roleCandidates: () => api.get("/pr/role-candidates"),
  approvalPolicy: (requestorEmpNo) => api.get("/pr/approval-policy", { params: requestorEmpNo ? { requestor_emp_no: requestorEmpNo } : {} }),
  masterData: () => api.get("/pr/master-data"),
  // Edit a PR — only valid while the original creator hasn't been actioned by
  // the requestor-review step (server enforces this). `editPayload` returns
  // the PR shaped exactly like the CreatePR form expects.
  editPayload: (vocNo) => api.get(`/prs/${vocNo}/edit-payload`),
  updatePR: (vocNo, body) => api.put(`/prs/${vocNo}`, body),
};

export const requestorReview = {
  enabled: () => api.get("/requestor-review/enabled"),
  myPRs: () => api.get("/requestor-review/my-prs"),
  decide: (vocNo, decision, reason) =>
    api.post(`/requestor-review/${vocNo}/decide`, { decision, reason: reason || null }),
};

export const webauthn = {
  status: (username) => api.get("/auth/webauthn/status", { params: username ? { username } : {} }),
  registerOptions: (deviceLabel) => api.post("/auth/webauthn/register/options", { device_label: deviceLabel || null }),
  registerVerify: (credential, deviceLabel) => api.post("/auth/webauthn/register/verify", { credential, device_label: deviceLabel || null }),
  loginOptions: (username) => api.post("/auth/webauthn/login/options", { username: username || null }),
  loginVerify: (credential) => api.post("/auth/webauthn/login/verify", { credential }),
  listDevices: () => api.get("/auth/webauthn/devices"),
  deleteDevice: (credentialId) => api.delete(`/auth/webauthn/devices/${encodeURIComponent(credentialId)}`),
};
